/**
 * Passkeys in the browser (passkeys + multi-method 2FA, spec §6.2–6.4).
 *
 * Thin wrappers over @simplewebauthn/browser and the /auth/passkey/*
 * endpoints:
 *  - signInWithPasskey  — passwordless sign-in (discoverable credential,
 *    optionally as browser autofill / conditional UI); resolves to the same
 *    union /auth/login returns, so the login page routes it identically.
 *  - verifyWithPasskey  — the second factor after a password, and step-up.
 *  - registerPasskey    — add a passkey to the signed-in person.
 *
 * A user cancelling the browser sheet (NotAllowedError) or an aborted
 * ceremony resolves to PASSKEY_CANCELLED — never an error toast. Every
 * other failure (server refusal, network) rejects as usual.
 *
 * The pure helpers (defaultPasskeyName, withPasskeyCancel,
 * secondFactorChoice) are unit-tested under Node (`npm run test:unit`).
 */

import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  startRegistration,
  WebAuthnAbortService,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import * as authApi from "@/lib/api/auth";
import type { LoginResult, TwoFactorMethodFlags } from "@/types/auth";

/** The browser sheet was dismissed or the ceremony was aborted. */
export interface PasskeyCancelled {
  cancelled: true;
}

export const PASSKEY_CANCELLED: PasskeyCancelled = Object.freeze({ cancelled: true as const });

export function isPasskeyCancelled(v: unknown): v is PasskeyCancelled {
  return typeof v === "object" && v !== null && (v as { cancelled?: unknown }).cancelled === true;
}

/** True for the errors a dismissed sheet / aborted ceremony raises.
 *  simplewebauthn's WebAuthnError keeps the DOMException's name. */
function isCancelError(err: unknown): boolean {
  if (typeof err !== "object" || err === null) return false;
  const { name, code } = err as { name?: unknown; code?: unknown };
  return name === "NotAllowedError" || name === "AbortError" || code === "ERROR_CEREMONY_ABORTED";
}

/** Runs a ceremony; a user cancel resolves to PASSKEY_CANCELLED. */
export async function withPasskeyCancel<T>(run: () => Promise<T>): Promise<T | PasskeyCancelled> {
  try {
    return await run();
  } catch (err) {
    if (isCancelError(err)) return PASSKEY_CANCELLED;
    throw err;
  }
}

/** True when this browser can run a passkey ceremony at all. */
export async function passkeySupported(): Promise<boolean> {
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
}

/** True when the browser can offer passkeys from the email field's
 *  autofill (conditional UI). */
export async function passkeyAutofillSupported(): Promise<boolean> {
  try {
    return await browserSupportsWebAuthnAutofill();
  } catch {
    return false;
  }
}

/** Stops a running ceremony (e.g. the login page's autofill one). */
export function cancelPasskeyCeremony(): void {
  WebAuthnAbortService.cancelCeremony();
}

/**
 * Passwordless sign-in. With `autofill`, the ceremony waits for the user to
 * pick a passkey from the email field's autofill list (needs an input with
 * autocomplete="… webauthn" on the page) and only resolves once they do.
 */
export async function signInWithPasskey(opts?: {
  autofill?: boolean;
  /** Checked after begin: false (the page moved on, or another ceremony
   *  replaced this one) skips the browser ceremony — resolves cancelled. */
  shouldContinue?: () => boolean;
}): Promise<LoginResult | PasskeyCancelled> {
  return withPasskeyCancel(async () => {
    const begin = await authApi.passkeyLoginBegin();
    if (opts?.shouldContinue && !opts.shouldContinue()) return PASSKEY_CANCELLED;
    const credential = await startAuthentication({
      optionsJSON: begin.options.publicKey as unknown as PublicKeyCredentialRequestOptionsJSON,
      useBrowserAutofill: opts?.autofill === true,
    });
    try {
      return await authApi.passkeyLoginFinish({ challenge_id: begin.challenge_id, credential });
    } catch (err) {
      // The person already picked a passkey: whatever the server says now
      // must reach them (round 2 F-I2).
      throw markPasskeyFinishError(err);
    }
  });
}

const FINISH_PHASE = "wrPasskeyPhase";

/** Tags an error from the finish request (after the person picked a
 *  passkey). The error object itself is kept so toApiError still reads it. */
export function markPasskeyFinishError(err: unknown): unknown {
  if (typeof err === "object" && err !== null) {
    try {
      Object.defineProperty(err, FINISH_PHASE, { value: "finish", configurable: true });
    } catch {
      /* frozen error: treated as a begin error */
    }
  }
  return err;
}

/** "finish" when the error came after the person picked a passkey. */
export function passkeyErrorPhase(err: unknown): "begin" | "finish" {
  return typeof err === "object" && err !== null && (err as Record<string, unknown>)[FINISH_PHASE] === "finish"
    ? "finish"
    : "begin";
}

export type PasskeyLoginErrorAction =
  /** 403 totp_enrollment_required: 2FA is mandatory and there is no
   *  method — route to the enrolment flow. */
  | { kind: "enrol" }
  /** Stay quiet, keep the password form (the autofill START failed). */
  | { kind: "quiet" }
  /** Tell the person, then offer autofill again. */
  | { kind: "toast"; message: string };

/**
 * Wait before re-arming autofill after the server refused a picked passkey:
 * 1.5 s, then 3 s, 6 s … capped at 15 s, so an authenticator that answers on
 * its own (a virtual one) cannot spin the login endpoint. `refusals` is how
 * many refusals have happened on this page so far (>= 1).
 */
export function autofillRestartDelayMs(refusals: number): number {
  const n = Math.max(1, Math.floor(Number.isFinite(refusals) ? refusals : 1));
  return Math.min(1500 * 2 ** (n - 1), 15_000);
}

/**
 * What the login page does with a failed passkey sign-in. A dismissed sheet
 * never gets here. The autofill ceremony starts on page load, so a failure
 * of its begin request (passkeys off, rate limit, network) stays quiet —
 * the person never asked for anything. Every failure after the person
 * picked a passkey (the finish request: locked, inactive account, server
 * error, network) and every failure of the button ceremony is shown.
 */
export function passkeyLoginErrorAction(input: {
  code: string;
  message: string;
  phase: "begin" | "finish";
  fromAutofill: boolean;
}): PasskeyLoginErrorAction {
  if (input.code === "totp_enrollment_required") return { kind: "enrol" };
  if (input.fromAutofill && input.phase === "begin" && input.code !== "invalid_challenge") {
    return { kind: "quiet" };
  }
  switch (input.code) {
    case "invalid_challenge":
      return { kind: "toast", message: "That sign-in request expired. Choose your passkey again." };
    case "network":
      return {
        kind: "toast",
        message: "We couldn't reach WorkRight to finish signing you in. Check your connection and try again.",
      };
    case "passkey_invalid":
      return {
        kind: "toast",
        message: input.message || "That passkey could not be used. Try again or sign in with your password.",
      };
    default:
      return {
        kind: "toast",
        message:
          input.message && input.message !== "Something went wrong" && input.message !== "Something went wrong."
            ? input.message
            : "Signing in with your passkey didn't work. Try again, or sign in with your password.",
      };
  }
}

/** Passkey names: 1–60 characters, none of them control, format (bidi,
 *  zero-width) or line/paragraph separators — the server refuses those
 *  too (400 validation, field "name"). Returns the problem, or null. */
export function passkeyNameProblem(raw: string): string | null {
  const name = raw.trim();
  const length = [...name].length;
  if (length < 1 || length > 60) return "Give the passkey a name of 1 to 60 characters.";
  if (/[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]/u.test(name)) {
    return "The passkey name can't contain line breaks or hidden characters. Use plain letters, numbers and spaces.";
  }
  return null;
}

/** The person has only a passkey and this browser can't run one: no code
 *  form can help, they need the device that holds the passkey. */
export function passkeyOnlyUnusable(methods: TwoFactorMethodFlags | null | undefined, supported: boolean): boolean {
  return methods?.passkey === true && methods.totp === false && !supported;
}

export const PASSKEY_UNUSABLE_MESSAGE =
  "This browser can't use passkeys, and a passkey is your only two-factor authentication method. Use the device or browser that has your passkey.";

/** The session's own passkey as the second factor / step-up. Resolves with
 *  the verify response (it may name companies this sign-in just joined). */
export async function verifyWithPasskey(): Promise<authApi.SecondFactorResponse | PasskeyCancelled> {
  return withPasskeyCancel(async () => {
    const begin = await authApi.passkeyVerifyBegin();
    const credential = await startAuthentication({
      optionsJSON: begin.options.publicKey as unknown as PublicKeyCredentialRequestOptionsJSON,
    });
    return authApi.passkeyVerifyFinish({ challenge_id: begin.challenge_id, credential });
  });
}

export interface RegisteredPasskey {
  id: number;
  name: string;
  /** This was the person's first second factor: the session's 2FA is now
   *  complete (continue as after /2fa-verify). */
  firstMethod: boolean;
}

/** Adds a passkey named `name` to the signed-in person. */
export async function registerPasskey(name: string): Promise<RegisteredPasskey | PasskeyCancelled> {
  return withPasskeyCancel(async () => {
    const begin = await authApi.passkeyRegisterBegin();
    const credential = await startRegistration({
      optionsJSON: begin.options.publicKey as unknown as PublicKeyCredentialCreationOptionsJSON,
    });
    const res = await authApi.passkeyRegisterFinish({
      challenge_id: begin.challenge_id,
      name,
      credential,
    });
    return { id: res.id, name: res.name, firstMethod: res.status === "ok" };
  });
}

/** True when a registration failed because this authenticator already
 *  holds a passkey for the account (excludeCredentials matched). */
export function isAlreadyRegisteredError(err: unknown): boolean {
  return typeof err === "object" && err !== null && (err as { name?: unknown }).name === "InvalidStateError";
}

function browserName(ua: string): string | null {
  if (/SamsungBrowser\//.test(ua)) return "Samsung Internet";
  if (/Edg(e|A|iOS)?\//.test(ua)) return "Edge";
  if (/OPR\/|Opera/.test(ua)) return "Opera";
  if (/Firefox\/|FxiOS\//.test(ua)) return "Firefox";
  if (/Chrome\/|CriOS\/|Chromium\//.test(ua)) return "Chrome";
  if (/Safari\//.test(ua) && /Version\//.test(ua)) return "Safari";
  return null;
}

function deviceName(ua: string): string | null {
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android";
  if (/Macintosh|Mac OS X/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows";
  if (/CrOS/.test(ua)) return "Chromebook";
  if (/Linux/.test(ua)) return "Linux";
  return null;
}

/** A default name for a new passkey from the user agent, e.g. "Chrome on
 *  Mac" (editable before saving; always 1–60 characters). */
export function defaultPasskeyName(userAgent?: string): string {
  const ua = userAgent ?? (typeof navigator !== "undefined" ? navigator.userAgent : "");
  const browser = browserName(ua);
  const device = deviceName(ua);
  if (browser && device) return `${browser} on ${device}`;
  if (device) return `Passkey on ${device}`;
  if (browser) return `${browser} passkey`;
  return "My passkey";
}

export interface SecondFactorChoice {
  /** What the prompt offers first. */
  primary: "passkey" | "totp";
  /** Also offer "Use authenticator code instead". */
  codeFallback: boolean;
}

/** Which second factor a 2FA / step-up prompt leads with: a passkey when
 *  the person has one and this browser can use it, the code otherwise. */
export function secondFactorChoice(
  methods: TwoFactorMethodFlags | null | undefined,
  supported: boolean,
): SecondFactorChoice {
  if (methods?.passkey && supported) {
    return { primary: "passkey", codeFallback: methods.totp };
  }
  return { primary: "totp", codeFallback: false };
}

/**
 * True when a sign-in / activate / picker response (or /me) left the
 * session PENDING 2FA (password accepted, second factor still owed →
 * /2fa-verify). The backend says so explicitly with `pending: true`
 * (alongside `methods`); an explicit boolean wins, and a body without it
 * falls back to the presence of `methods` (only the pending body carries
 * them). `totp_required` alone is NOT the signal: the backend sets it
 * whenever 2FA applies to the person — also after a passkey sign-in or a
 * company switch that already completed it.
 */
export function secondFactorPending(res: {
  pending?: boolean | null;
  methods?: TwoFactorMethodFlags | null;
}): boolean {
  if (typeof res.pending === "boolean") return res.pending;
  return res.methods != null;
}
